import { Component, OnInit, Input, OnChanges, OnDestroy } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { EChartOption } from "echarts";

import { Observable, Subscription } from "rxjs";
import { DashboardThemeService } from "../../services/dashboard-theme/dashboard-theme.service";

import { FormUtils } from "../../utils/formUtils";
import { columnConfig } from "src/app/models/common/column-Config";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() source: Observable<any[]>;
  @Input() type = "bar";
  @Input() graphType = "bar";
  @Input() fieldRule: any;
  @Input() validationRule: any;
  @Input() title = "";
  @Input() subTitle = "";
  @Input() dataFormatFunction: Function;
  @Input() measurements: any;
  @Input() top: any;
  @Input() options: any;
  @Input() metrics: Function;
  @Input() topFunction: Function;
  @Input() isLoading: Observable<boolean>;

  theme: any;

  echartsInstance: any;
  dashboardFromGroup: FormGroup;
  count: number = 0;
  sourceSubs: Subscription;
  sourceDataSet: any[] = [];
  status: boolean = true;

  barChartStatus: boolean = false;
  pieChartStatus: boolean = true;
  verticalBarChartStatus: boolean = true;
  doughnutChartStatus: boolean = true;

  lineChartStatus: boolean = true;
  sortStatus: boolean = true;
  showDisplay: boolean = false;
  selected: any;
  selectedTop: any;
  gridConfiguration: columnConfig[] = [];
  showMetricPieChart: boolean;
  metricChartTitle: any;
  selectedOption: any;

  tableStatus = true;
  displayedColumns: string[] = ['name', 'label', 'value'];

  constructor(
    private fb: FormBuilder,
    private dashboardTheme: DashboardThemeService,
    private translate: TranslateService
  ) {
    this.dashboardFromGroup = this.fb.group({
      measurementsCombo: new FormControl("", []),
      topCombo: new FormControl("", []),
    });
  }

  chartOption: EChartOption;
  typeChart: any = "bar";

  ngOnInit(): void {
    if (this.options?.propertyValue !== undefined) {
      this.selectedOption = `${this.options?.propertyValue}`;
    } else {
      this.selectedOption = `${this.options?.propertyName}`;
    }

    this.metricChartTitle = this.measurements.find(
      (x) => x.code === this.selectedOption
    );
    this.metricChartTitle = this.metricChartTitle?.description;

    setTimeout(() => {
      this.dashboardFromGroup.patchValue(
        {
          measurementsCombo: this.selectedOption ? this.selectedOption : [],
        },
        { emitEvent: true, onlySelf: false }
      );
    });

    if (this.source) {
      this.sourceSubs = this.source.subscribe((items: any) => {
        if (items !== null && items !== undefined) {
          this.sourceDataSet = items;

          if (this.type === "bar" && this.graphType === "bar") {
            this.barChartStatus = false;
            this.verticalBarChartStatus = true;
            this.pieChartStatus = true;
          }
          if (this.type === "bar" && this.graphType === "verticalBar") {
            this.barChartStatus = true;
            this.verticalBarChartStatus = false;
            this.pieChartStatus = true;
          }
          if (this.type === "pie" && this.graphType === "pie") {
            this.pieChartStatus = false;
            this.verticalBarChartStatus = true;
            this.barChartStatus = true;
          }
          if (this.showDisplay) {
            this.pieChartStatus = true;
            this.barChartStatus = true;
          }

          this.handleChartData();
        }
      });
    }
    this.dashboardTheme.theme$.subscribe((theme) => {
      this.theme = theme;
    });
  }

  selectTop(event: any) {
    if (event?.value && event?.value !== undefined) {
      if (this.top.length > 0 && this.top) {
        this.selected = this.measurements.find(
          (x) =>
            x.code === this.dashboardFromGroup.controls.measurementsCombo.value
        );

        this.selectedTop = this.top.find((x) => x.code === event.value);
        this.sourceSubs = this.source.subscribe((items: any) => {
          if (items !== null && items !== undefined) {
            this.sourceDataSet = items;
            this.handleChartData();
          }
        });
      }
    } else {
      this.selectedTop = undefined;
      this.sourceSubs = this.source.subscribe((items: any) => {
        if (items !== null && items !== undefined) {
          this.sourceDataSet = items;
          this.handleChartData();
        }
      });
    }
  }

  selectMeasurement(event: any) {
    if (event.value && event.value !== undefined) {
      if (this.measurements.length > 0 && this.measurements) {
        this.selected = this.measurements.find((x) => x.code === event.value);
        this.metricChartTitle = this.selected?.description;
        this.showMetricPieChart = this.metrics(
          this.measurements,
          this.selected
        );
        this.sourceSubs = this.source.subscribe((items: any) => {
          if (items !== null && items !== undefined) {
            this.sourceDataSet = items;
            this.handleChartData();
          }
        });
      }
    }
  }

  handleChartData() {
    let selectedField: string = "";
    let selectedProperty: string = "";
    let handleDuplicates: boolean = false;

    if (this.selected === undefined) {
      selectedField = this.options?.propertyName;
      selectedProperty = this.options?.propertyValue;
      handleDuplicates = this.options?.handleDuplicates;
    } else {
      selectedField = this.selected?.field;
      selectedProperty = this.selected?.property;
      handleDuplicates = this.selected?.handleDuplicates;
    }

    this.sourceDataSet = this.dataFormatFunction(
      this.sourceDataSet,
      selectedField,
      selectedProperty,
      this.options?.filterRule
    );

    if (this.sortStatus) {
      this.sourceDataSet = this.sourceDataSet.sort(function (a, b) {
        return parseFloat(a?.value) - parseFloat(b?.value);
      });
    } else {
      this.sourceDataSet = this.sourceDataSet.sort(function (a, b) {
        return parseFloat(b?.value) - parseFloat(a?.value);
      });
    }

    if (handleDuplicates) {
      const ids = this.sourceDataSet.map((o) => o.name);
      this.sourceDataSet = this.sourceDataSet.filter(
        ({ name }, index) => !ids.includes(name, index + 1)
      );
    }

    if (this.topFunction === undefined && this.selectedTop === undefined) {
      //Console don't do a thing
    } else {
      this.sourceDataSet = this.topFunction(
        this.sourceDataSet,
        this.selectedTop
      );
    }

    this.buildChart();
  }

  sortChart() {
    this.sortStatus = !this.sortStatus;
    this.sourceSubs = this.source.subscribe((items: any) => {
      if (items !== null && items !== undefined) {
        this.sourceDataSet = items;
        if (
          this.selected?.field === undefined &&
          this.selected?.property === undefined
        ) {
          this.selected = {
            field: this.options?.propertyName,
            property: this.options?.propertyValue,
          };
        }

        this.handleChartData();
      }
    });
  }

  changeBarChart() {
    this.showDisplay = false;
    this.barChartStatus = false;
    this.pieChartStatus = true;
    this.doughnutChartStatus = true;
    this.verticalBarChartStatus = true;
    this.tableStatus = true;
    this.type = "bar";
    this.graphType = "bar";
    this.buildChart();
  }

  changePieChart() {
    this.showDisplay = false;
    this.barChartStatus = true;
    this.pieChartStatus = false;
    this.doughnutChartStatus = true;
    this.verticalBarChartStatus = true;
    this.tableStatus = true;
    this.type = "pie";
    this.graphType = "pie";
    this.buildChart();
  }

  changeDoughnutChart() {
    this.showDisplay = false;
    this.barChartStatus = true;
    this.pieChartStatus = true;
    this.doughnutChartStatus = false;
    this.verticalBarChartStatus = true;
    this.tableStatus = true;
    this.type = "doughnut";
    this.graphType = "doughnut";
    this.buildChart();
  }

  changeBarVerticalChart() {
    this.showDisplay = false;
    this.barChartStatus = true;
    this.pieChartStatus = true;
    this.doughnutChartStatus = true;
    this.verticalBarChartStatus = false;
    this.tableStatus = true;
    this.type = "bar";
    this.graphType = "verticalBar";
    this.buildChart();
  }

  changeTable() {
    this.showDisplay = false;
    this.barChartStatus = true;
    this.pieChartStatus = true;
    this.doughnutChartStatus = true;
    this.verticalBarChartStatus = true;
    this.tableStatus = false;
  }

  buildChart() {
    switch (this.graphType) {
      case "bar":
        this.buildBarChart();
        break;
      case "pie":
        this.showMetricPieChart = this.metrics(
          this.measurements,
          this.selected
        );

        if (this.showMetricPieChart) {
          this.buildPieChart();
        } else {
          this.pieChartStatus = true;
          this.barChartStatus = false;
          this.type = "bar";
          this.buildBarChart();
        }
        break;
      case "doughnut":
        this.buildDoughnutChart();
        break;
      case "verticalBar":
        this.type = "bar";
        this.buildVerticalBarChart();
        break;
      default:
        break;
    }
  }

  buildVerticalBarChart() {
    this.chartOption = {
      title: {
        left: "center",
      },
      tooltip: {
        trigger: "item",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (data: any) {
          return `${data?.seriesName}: ${data?.data?.value} ${data?.data?.prop1 ? ' - ' + data?.data?.prop1 : ''}`;
        },
      },
      legend: {
        data: this.getLegend(),
        type: "scroll",
        orient: "horizontal",
        left: "left",
        top: "top",
        bottom: 10,
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: [`${this.metricChartTitle}`],
        axisLabel: {
          rotate: 0,
        },
      },
      yAxis: {
        type: "value",
        boundaryGap: [0, 0.01],
      },
      series: this.getVerticalBarSeries(),
    };
  }

  buildDoughnutChart() {
    this.chartOption = {
      tooltip: {
        trigger: "item",
        formatter: function (data: any) {
          return `${data?.data?.name}: ${data?.data?.value} ${data?.data?.prop1 ? ' - ' + data?.data?.prop1 : ''}</br> (${data?.percent}%)`;
        },
      },
      legend: {
        orient: "horizontal",
        left: "left",
        top: "top",
        type: "scroll",
        data: this.getPieLegend(),
      },
      series: [
        {
          name: this.metricChartTitle,
          type: "pie",
          radius: ["50%", "70%"],
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: "center",
          },
          emphasis: {
            label: {
              show: true,
              fontWeight: "bold",
              formatter: function (data: any) {
                return `${data?.data?.name}`;
              },
            },
          },
          labelLine: {
            show: false,
          },
          data: this.getPieSeries(),
        },
      ],
    };
  }

  buildPieChart() {
    this.chartOption = {
      title: {
        subtext: this.subTitle,
        left: "center",
        top: "30",
      },
      tooltip: {
        trigger: "item",
        formatter: function (data: any) {
          return `${data?.data?.name}: ${data?.data?.value} ${data?.data?.prop1 ? ' - ' + data?.data?.prop1 : ''}</br> (${data?.percent}%)`;
        },
      },
      legend: {
        type: "scroll",
        orient: "horizontal",
        left: "left",
        top: "top",
        data: this.getPieLegend(),
      },
      series: [
        {
          name: this.metricChartTitle,
          type: "pie",
          radius: "55%",
          center: ["50%", "50%"],
          data: this.getPieSeries(),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  }

  buildBarChart() {
    this.chartOption = {
      title: {
        left: "center",
      },
      tooltip: {
        trigger: "item",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (data: any) {
          return `${data?.seriesName}: ${data?.data?.value} ${data?.data?.prop1 ? ' - ' + data?.data?.prop1 : ''}`;
        },
      },
      legend: {
        data: this.getLegend(),
        type: "scroll",
        orient: "horizontal",
        left: "left",
        top: "top",
        bottom: 10,
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        boundaryGap: [0, 0.01],
      },
      yAxis: {
        type: "category",
        data: [`${this.metricChartTitle}`],
        // inverse: true,
        axisLabel: {
          rotate: 90,
        },
      },
      series: this.getBarSeries(),
    };
  }

  getLegend(): any[] {
    const legends: any[] = [];
    if (this.sourceDataSet && this?.sourceDataSet.length > 0) {
      this.sourceDataSet.forEach((item) => {
        legends.push(item?.legend);
      });
    }

    return legends;
  }

  getPieLegend(): any[] {
    const legends: any[] = [];
    if (this.sourceDataSet && this?.sourceDataSet.length > 0) {
      this.sourceDataSet.forEach((item) => {
        if (item?.value > 0) {
          legends.push(item?.legend);
        }
      });
    }

    return legends;
  }

  getPieSeries(): any[] {
    const series: any[] = [];

    if (this.sourceDataSet && this.sourceDataSet.length > 0) {
      this.sourceDataSet.forEach((item) => {
        if (item?.value > 0) {
          let serieOptions: any;
          serieOptions = {
            name: item?.name,
            value: item?.value,
            prop1: item?.fuelType,
          };
          series.push(serieOptions);
        }
      });
    } else {
      series.push({
        value: 0,
        name: this.translate.instant("DashBoard.NoData"),
        itemStyle: { color: "gray" },
      });
    }
    return series;
  }

  getVerticalBarSeries(): any[] {
    const series: any[] = [];
    if (this.sourceDataSet && this.sourceDataSet.length > 0) {
      this.sourceDataSet.forEach((item) => {
        let serieOptions: any;
        serieOptions = {
          name: item?.name,
          type: this.type,
          animation: true,
          label: {
            show: true,
            position: "top",
            fontWeight: 400,
          },
          animationDelay: 0,
          data: [
            {
              value: item.value,
              prop1: item?.fuelType, 
            },
          ],
        };
        series.push(serieOptions);
      });
    }
    return series;
  }

  getBarSeries(): any[] {
    const series: any[] = [];
    if (this.sourceDataSet && this.sourceDataSet.length > 0) {
      this.sourceDataSet.forEach((item) => {
        let serieOptions: any;
        serieOptions = {
          name: item?.name,
          type: this.type,
          animation: true,
          label: {
            show: true,
            position: "right",
            fontWeight: 400,
          },
          animationDelay: 0,
          data: [{ value: item.value, prop1: item?.fuelType }],
        };
        series.push(serieOptions);
      });
    }
    return series;
  }

  ngOnDestroy() {
    if (this.sourceSubs) {
      this.sourceSubs.unsubscribe();
    }
  }

  onChartInit(ec) {
    this.echartsInstance = ec;
  }

  getChartImg() {
    let img = new Image();
    img.src = this.echartsInstance.getDataURL({
      pixelRatio: 2,
      backgroundColor: "#fff",
    });
    const metricDescription = this.measurements.find(
      (x) => x.code == this.dashboardFromGroup.controls.measurementsCombo.value
    );
    const currentDate = FormUtils.datePickerFormatterTime(new Date());

    let a = document.createElement("a");
    a.href = img.src;
    a.download = `${this.subTitle}-${metricDescription?.description}${currentDate}.png`;
    a.click();
  }

  showNoData(data: any[]) {
    if (data.length === 0 && this.subTitle.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  ngOnChanges() {
    if (this.selected !== undefined) {
      this.metricChartTitle = this.selected?.description;
      setTimeout(() => {
        this.dashboardFromGroup.patchValue(
          {
            measurementsCombo: this.selected ? this.selected?.code : [],
          },
          { emitEvent: true, onlySelf: false }
        );
      });
    } else {
      let getMeasurement = this.measurements.find(
        (x) => x.code == this.selectedOption
      );
      this.metricChartTitle = getMeasurement?.description;
      setTimeout(() => {
        this.dashboardFromGroup.patchValue(
          {
            measurementsCombo: this.selectedOption ? this.selectedOption : [],
          },
          { emitEvent: true, onlySelf: false }
        );
      });
    }

    this.buildChart();
  }

  removeDuplicates(originalArray, prop) {
    var newArray = [];
    var lookupObject = {};

    for (var i in originalArray) {
      lookupObject[originalArray[i][prop]] = originalArray[i];
    }

    for (i in lookupObject) {
      newArray.push(lookupObject[i]);
    }
    return newArray;
  }
}
